/**
 * `IndexerPipelineCard` — sub-card rendered above the regular endpoints
 * list when a service exposes one or more `(freshness)` probes.
 *
 * Each row shows: method badge, stripped path (the ` (freshness)` suffix is
 * a server-side marker, not user-facing), current lag (mono), p95 / max
 * subtitle (mono), and a status dot driven by the backend's classifier.
 *
 * Visual rules: hairline borders, mono only for numbers/timestamps per
 * DESIGN.md §3. No new color tokens.
 */
import clsx from 'clsx';
import { memo, type KeyboardEvent } from 'react';
import {
  parseEndpointKey,
  stripFreshnessSuffix,
} from '../lib/endpoint-key';
import { lagToTone, pipelineFromEndpointKey } from '../lib/lag-bars';
import { expectedBucketCount } from '../lib/lag-pad';
import {
  formatCurrentLagTooltip,
  formatSummaryTooltip,
} from '../lib/lag-tooltips';
import {
  LagHistoryBar,
  type LagBucketView,
  type WindowSel,
} from './LagHistoryBar';

const WINDOW_LABEL: Record<WindowSel, string> = {
  '90d': 'last 90 days',
  '24h': 'last 24 hours',
  '1h': 'last 1 hour',
};

type BucketStatus = 'ok' | 'warn' | 'down' | 'no_data';

export interface FreshnessEndpoint {
  endpointKey: string;
  status: BucketStatus;
  currentLagSec?: number | null;
  avgLagSec?: number | null;
  p95LagSec?: number | null;
  maxLagSec?: number | null;
  /**
   * Block-gap stats — only populated for `(freshness)` probes targeting a
   * sequence-numbered table (actions/traces/transactions). `null` for the
   * masterchain probe itself (tip-vs-tip is tautologically 0). When `null`,
   * the subtitle hides the block annotation.
   */
  currentLagBlocks?: number | null;
  avgLagBlocks?: number | null;
  p95LagBlocks?: number | null;
  maxLagBlocks?: number | null;
  /**
   * Per-bucket lag history. Length matches the uptime window grid
   * (`60 / 96 / 90` per `1h / 24h / 90d`). Only present for `(freshness)`
   * endpoints; null/undefined for non-freshness rows.
   */
  lagBuckets?: readonly LagBucketView[] | null;
}

export interface IndexerPipelineCardProps {
  endpoints: readonly FreshnessEndpoint[];
  windowSel: WindowSel;
  /**
   * When provided, each freshness row becomes a clickable target that opens
   * the lag detail modal (owned by the parent `EndpointsBlock`). Omitted →
   * rows stay read-only (e.g. in isolated rendering).
   */
  onOpenDetail?: (endpointKey: string) => void;
}

type Tone = 'ok' | 'warn' | 'down' | 'none';

const TONE_DOT_CLASSES: Record<Tone, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  down: 'bg-down',
  none: 'bg-paper-3 border border-line',
};

export function formatLag(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) {
    // Lag carries sub-second precision (1-decimal) from the backend. Render
    // the decimal (e.g. "0.4s", "2.4s"); whole values drop the ".0". A value
    // that rounds to zero shows as "<0.1s" rather than a bare "0s" — the
    // probe still measures against the block's whole-second gen_utime, so a
    // literal zero is unknowable and "0s" would read too good.
    const r = Math.round(seconds * 10) / 10;
    if (r <= 0) return '<0.1s';
    return `${r}s`;
  }
  // >= 60s: minutes + whole seconds; sub-second noise is irrelevant here.
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

function IndexerPipelineCardImpl({
  endpoints,
  windowSel,
  onOpenDetail,
}: IndexerPipelineCardProps) {
  if (endpoints.length === 0) return null;
  const expectedCount = expectedBucketCount(windowSel);
  const canOpen = onOpenDetail !== undefined;
  return (
    <section
      aria-label="Indexer pipeline freshness"
      className="border-t border-line bg-paper-2 px-4 pt-3 pb-3 sm:px-5 sm:pl-12"
    >
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-3 sm:text-[11.5px]">
        Indexer pipeline freshness
      </div>
      {endpoints.map((ep, idx) => {
        const parsed = parseEndpointKey(ep.endpointKey);
        const displayPath = stripFreshnessSuffix(parsed.path);
        const isLast = idx === endpoints.length - 1;
        const pipeline = pipelineFromEndpointKey(ep.endpointKey);
        // Pill in this card reflects FRESHNESS (lag), not uptime — uptime is
        // already shown by the dot in the regular endpoints card above. With
        // the prior `TONE_BY_STATUS[ep.status]` mapping, a single failed
        // probe in the window painted the pill red even when every successful
        // probe reported a healthy 2-3s lag (visible in the green
        // LagHistoryBar). That mismatched the card's title and confused the
        // operator. lagToTone classifies by `currentLagSec` against the
        // per-pipeline thresholds — same signal the history bars encode.
        const tone = lagToTone(ep.currentLagSec ?? null, pipeline);
        const lagBuckets = ep.lagBuckets ?? null;
        const open = () => {
          onOpenDetail?.(ep.endpointKey);
        };
        const interactive = canOpen
          ? {
              role: 'button' as const,
              tabIndex: 0,
              'aria-label': `Open lag detail for ${displayPath}`,
              onClick: open,
              onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  open();
                }
              },
            }
          : {};
        return (
          <div
            key={ep.endpointKey}
            {...interactive}
            className={clsx(
              'flex flex-col py-2.5',
              !isLast && 'border-b border-line',
              canOpen &&
                'cursor-pointer rounded-md outline-none transition-[outline-color] hover:outline hover:outline-1 hover:-outline-offset-1 hover:outline-line-strong focus-visible:shadow-ring-blue',
            )}
          >
            <div
              data-testid="freshness-row"
              className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-2 text-[13px] sm:gap-3.5 sm:text-[13.5px]"
            >
              {parsed.method ? (
                <span className="rounded border border-line bg-paper-1 px-1.5 py-0.5 font-mono text-[10.5px] font-semibold tracking-wide text-ink-2">
                  {parsed.method}
                </span>
              ) : (
                <span aria-hidden />
              )}
              <div className="min-w-0">
                <div className="truncate font-medium text-ink-1">
                  {displayPath}
                </div>
                <div
                  className="mt-0.5 truncate font-mono text-[11px] text-ink-3 [font-feature-settings:'tnum']"
                  title={formatSummaryTooltip(
                    ep.p95LagSec,
                    ep.maxLagSec,
                    pipeline,
                    WINDOW_LABEL[windowSel],
                  )}
                >
                  {`p95 ${formatLag(ep.p95LagSec)} · max ${formatLag(ep.maxLagSec)}`}
                </div>
              </div>
              <div
                className="cursor-help font-mono text-[13px] font-semibold text-ink-1 [font-feature-settings:'tnum']"
                aria-label={`current lag ${formatLag(ep.currentLagSec)}`}
                title={formatCurrentLagTooltip(ep.currentLagSec, pipeline)}
              >
                {formatLag(ep.currentLagSec)}
              </div>
              <span
                className={clsx(
                  'h-2.5 w-2.5 rounded-full',
                  TONE_DOT_CLASSES[tone]
                )}
                aria-hidden
              />
            </div>
            {lagBuckets && (
              <div className="mt-2">
                <LagHistoryBar
                  buckets={lagBuckets}
                  pipeline={pipeline}
                  windowSel={windowSel}
                  expectedCount={expectedCount}
                  endpointKey={ep.endpointKey}
                />
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}

export const IndexerPipelineCard = memo(IndexerPipelineCardImpl);
